import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { GripVertical, Plus, X, ChevronRight } from "lucide-react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { fromJS } from "immutable";
import TaskAction from "../../../actions/TaskAction";
import FilterDropdown from "../filter/FilterDropdown";
import Container from "../../../board/dnd/Container";
import Draggable from "../../../board/dnd/Draggable";

interface GridColumnSettingsProps {
  contentType: string;
}

const GridColumnSettings = ({ contentType }: GridColumnSettingsProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const tenantInfo = useSelector((state: any) => state.tenant.get("tenantInfo"));
  const isJira = tenantInfo.getIn(["host", "product"]) === "JIRA";
  const fields =
    useSelector((state: any) => state.projectReducer.getIn(["project", "fields"])) || fromJS([]);
  const columns =
    useSelector((state: any) => state.grid.getIn(["viewSetting", contentType, "columns"])) ||
    fromJS([]);
  const gridFields = useSelector((state: any) => state.taskReducer.get("gridFields")) || fromJS([]);

  const allColumns = useMemo(() => {
    return fields
      .filter((field) => field.get("formType") == contentType)
      .map((field) => {
        const columnData = columns.find((column) => column.get("selector") === field.get("id"));
        if (columnData) return columnData;
        return fromJS({
          name: field.get("label"),
          selector: field.get("id"),
          width: 250,
          show: false,
          isCustomField: field.get("isCustomField"),
          formType: field.get("formType"),
          key: field.get("id"),
          id: field.get("id"),
        });
      });
  }, [fields, columns, contentType]);

  const visibleFields = useMemo(() => {
    let visibleFieldsData = [];
    if (gridFields.size > 0) {
      visibleFieldsData = gridFields.toJS();
    } else {
      visibleFieldsData = columns.filter((item) => item.get("show")).toJS();
    }
    return visibleFieldsData.filter((visibleField) => isJira || visibleField.selector !== "issue");
  }, [gridFields, columns, isJira]);

  const invisibleFields = useMemo(() => {
    let visibleFieldIds = [];
    if (gridFields.size > 0) {
      visibleFieldIds = gridFields.map((field) => field.get("selector")).toJS();
    } else {
      visibleFieldIds = columns
        .filter((item) => item.get("show"))
        .map((field) => field.get("selector"))
        .toJS();
    }
    return allColumns
      .filter(
        (column) =>
          (isJira || column.get("selector") !== "issue") &&
          !visibleFieldIds.includes(column.get("selector"))
      )
      .toJS();
  }, [gridFields, allColumns, columns, isJira]);

  const onDrop = (dropResult) => {
    const { removedIndex, addedIndex } = dropResult;
    if (removedIndex === null || addedIndex === null) return;

    // Default first for field name
    if (addedIndex === 0) return;

    const updated = [...visibleFields];
    const [moved] = updated.splice(removedIndex, 1);
    updated.splice(addedIndex, 0, moved);

    dispatch(TaskAction.changeGridFields([...updated]));
  };

  const handleRemove = (index: number) => {
    const newSortList = visibleFields.filter((_, idx) => idx !== index);
    dispatch(TaskAction.changeGridFields([...newSortList]));
  };

  return (
    <>
      <Container
        onDrop={onDrop}
        dragHandleSelector={".drag-handle"}
        nonDragAreaSelector={".no-drag"}
      >
        {visibleFields.map((item, index) => {
          const isDisableField = item.selector === "name";
          return (
            <Draggable key={`${item.selector}_${index}`}>
              <div className="w-full flex items-center justify-between gap-2 p-1 rounded-lg hover:bg-gray-100">
                <div
                  className={`w-full flex items-center gap-2 ${isDisableField ? "no-drag" : "drag-handle"}`}
                >
                  <div className={isDisableField ? "" : "cursor-move"}>
                    <GripVertical className="w-4 h-4" />
                  </div>
                  <div className="cursor-default text-sm">
                    <span>{item.name}</span>
                  </div>
                </div>
                {isDisableField ? (
                  <div className="drag-handle"></div>
                ) : (
                  <div className="no-drag p-1 flex items-center rounded-md hover:bg-gray-100">
                    <X
                      className={"w-4 h-4 text-gray-400 cursor-pointer"}
                      onClick={() => {
                        handleRemove(index);
                      }}
                    />
                  </div>
                )}
              </div>
            </Draggable>
          );
        })}
      </Container>
      {invisibleFields.length > 0 && (
        <div className="w-full pt-2 border-t border-gray-300">
          <Popover className="w-full">
            <PopoverButton className="w-full flex items-center justify-between cursor-pointer outline-none">
              <div className="px-2 text-sm">
                <div className="flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  {t("Add card row")}
                </div>
              </div>
              <ChevronRight className="w-5 h-5" />
            </PopoverButton>
            <PopoverPanel
              anchor="right start"
              className="[--anchor-gap:12px] z-20 p-2 bg-white rounded-md border border-gray-300 shadow-xl outline-none"
            >
              {({ close }) => (
                <FilterDropdown
                  fields={invisibleFields.map((item) => ({
                    label: item.name,
                    value: item.selector,
                    data: fields.find((field) => field.get("id") === item.selector)?.toJS() || {
                      formType: item.formType,
                      id: item.selector,
                      label: item.name,
                    },
                  }))}
                  isFilter={false}
                  onClick={(value) => {
                    const newVisibleField = allColumns.find(
                      (column) => column.get("selector") === value.fieldId
                    );
                    if (newVisibleField) {
                      dispatch(
                        TaskAction.changeGridFields([...visibleFields, newVisibleField.toJS()])
                      );
                    }
                  }}
                  onClose={close}
                />
              )}
            </PopoverPanel>
          </Popover>
        </div>
      )}
    </>
  );
};

export default GridColumnSettings;
